import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, AlertTriangle } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface SkipJobDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    /** Shown in the dialog body for context (e.g. the job title). */
    jobLabel?: string | null;
    /** Called after a successful "cannot complete" so the caller can refresh. */
    onDone?: () => void;
}

/**
 * H23: confirmation dialog for marking a job "cannot complete" (the old
 * one-click "Skip job"). A reason is required for everyone — the action routes
 * through POST /api/jobs/:id/cannot-complete, which records the reason, audits
 * the transition, and sends the job to Office Review. This replaces the
 * instant, reasonless misclick path.
 */
export function SkipJobDialog({ open, onClose, jobId, jobLabel, onDone }: SkipJobDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");

    const skipJob = useMutation({
        mutationFn: () => api.post(`/api/jobs/${jobId}/cannot-complete`, { reason: reason.trim() }),
        onSuccess: () => {
            toast.success("Marked cannot complete — sent to Close Out");
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["work-pulse"] });
            qc.invalidateQueries({ queryKey: ["office-review-queue"] });
            setReason("");
            onDone?.();
            onClose();
        },
        onError: (err: any) => {
            const msg = err?.body?.message ?? err?.message ?? "unknown error";
            toast.error(`Could not mark cannot complete: ${msg}`);
        },
    });

    const canSubmit = reason.trim().length > 0 && !skipJob.isPending;

    return (
        <Dialog open={open} onClose={onClose} title="Mark Cannot Complete">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Mark {jobLabel ? <span className="font-medium">{jobLabel}</span> : "this job"} as cannot complete?
                    It will leave the schedule and go to Close Out for follow-up.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="skip-reason">Reason *</Label>
                    <Textarea
                        id="skip-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why can't this job be completed? (e.g. no access, customer not home, wrong device)"
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={skipJob.isPending}>
                        Keep Job
                    </Button>
                    <Button
                        className="bg-amber-700 hover:bg-amber-800 text-white"
                        disabled={!canSubmit}
                        onClick={() => skipJob.mutate()}
                    >
                        {skipJob.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <AlertTriangle className="h-4 w-4 mr-1" />
                        )}
                        Mark Cannot Complete
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
